import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const PolicyImpactCalculator = () => {
  const [companySize, setCompanySize] = useState('');
  const [productCount, setProductCount] = useState('');
  const [deviceCategory, setDeviceCategory] = useState('');
  const [policyChanges, setPolicyChanges] = useState('');
  const [showResults, setShowResults] = useState(false);

  const companySizeOptions = [
    { value: '', label: 'Select company size' },
    { value: 'startup', label: 'Startup (1-50 employees)' },
    { value: 'small', label: 'Small (51-200 employees)' },
    { value: 'medium', label: 'Medium (201-1000 employees)' },
    { value: 'large', label: 'Large (1001-5000 employees)' },
    { value: 'enterprise', label: 'Enterprise (5000+ employees)' }
  ];

  const deviceCategoryOptions = [
    { value: '', label: 'Select device category' },
    { value: 'cardiovascular', label: 'Cardiovascular Devices' },
    { value: 'orthopedic', label: 'Orthopedic Implants' },
    { value: 'surgical', label: 'Surgical Instruments' },
    { value: 'diagnostic', label: 'Diagnostic Equipment' },
    { value: 'neurology', label: 'Neurological Devices' }
  ];

  const calculateImpact = () => {
    if (companySize && productCount && deviceCategory && policyChanges) {
      setShowResults(true);
    }
  };

  const resetCalculator = () => {
    setCompanySize('');
    setProductCount('');
    setDeviceCategory('');
    setPolicyChanges('');
    setShowResults(false);
  };

  const getCompanySizeMultiplier = () => {
    const multipliers = {
      startup: 1.2,
      small: 1.5,
      medium: 2.0,
      large: 3.5,
      enterprise: 5.0
    };
    return multipliers?.[companySize] || 1;
  };

  const calculateMetrics = () => {
    const baseHours = parseInt(policyChanges) * 8;
    const multiplier = getCompanySizeMultiplier();
    const productMultiplier = Math.ceil(parseInt(productCount) / 10);
    
    const hoursLost = Math.round(baseHours * multiplier * productMultiplier);
    const costPerHour = 75;
    const financialImpact = hoursLost * costPerHour;
    const riskScore = Math.min(Math.round((hoursLost / 100) * 10), 100);
    const timeSaved = Math.round(hoursLost * 0.85);
    const roiPercentage = Math.round((timeSaved * costPerHour / 50000) * 100);

    return {
      hoursLost,
      financialImpact,
      riskScore,
      timeSaved,
      roiPercentage
    };
  };

  const metrics = showResults ? calculateMetrics() : null;

  return (
    <section className="bg-muted py-12 md:py-16 lg:py-20 px-4 md:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-3 md:mb-4">
            Policy Impact Calculator
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
            Estimate the business impact of policy changes and potential ROI with PolicyAlerts
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8">
            <h3 className="text-lg md:text-xl font-semibold text-foreground mb-6">
              Enter Your Details
            </h3>

            <div className="space-y-4 md:space-y-6">
              <Select
                label="Company Size"
                description="Select your organization size"
                options={companySizeOptions}
                value={companySize}
                onChange={setCompanySize}
                required
              />

              <Input
                type="number"
                label="Number of Products"
                description="Total medical devices in your portfolio"
                placeholder="e.g., 25"
                value={productCount}
                onChange={(e) => setProductCount(e?.target?.value)}
                min="1"
                required
              />

              <Select
                label="Primary Device Category"
                description="Main category of your medical devices"
                options={deviceCategoryOptions}
                value={deviceCategory}
                onChange={setDeviceCategory}
                required
              />

              <Input
                type="number"
                label="Monthly Policy Changes"
                description="Average policy updates affecting your products"
                placeholder="e.g., 15"
                value={policyChanges}
                onChange={(e) => setPolicyChanges(e?.target?.value)}
                min="1"
                required
              />

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button
                  variant="default"
                  fullWidth
                  onClick={calculateImpact}
                  disabled={!companySize || !productCount || !deviceCategory || !policyChanges}
                  iconName="Calculator"
                  iconPosition="left"
                >
                  Calculate Impact
                </Button>
                <Button
                  variant="outline"
                  fullWidth
                  onClick={resetCalculator}
                  iconName="RotateCcw"
                  iconPosition="left"
                >
                  Reset
                </Button>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8">
            <h3 className="text-lg md:text-xl font-semibold text-foreground mb-6">
              Impact Analysis
            </h3>

            {!showResults ? (
              <div className="flex flex-col items-center justify-center py-12 md:py-16 text-center">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-muted rounded-full flex items-center justify-center mb-4">
                  <Icon name="Calculator" size={32} className="text-muted-foreground" />
                </div>
                <h4 className="text-base md:text-lg font-semibold text-foreground mb-2">
                  Ready to Calculate
                </h4>
                <p className="text-sm text-muted-foreground max-w-md">
                  Fill in your details and click "Calculate Impact" to see your personalized policy impact analysis
                </p>
              </div>
            ) : (
              <div className="space-y-4 md:space-y-6">
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 md:p-6">
                  <div className="flex items-start gap-3 mb-3">
                    <Icon name="AlertTriangle" size={24} color="var(--color-destructive)" />
                    <div className="flex-1">
                      <h4 className="text-base md:text-lg font-semibold text-foreground mb-1">
                        Without PolicyAlerts
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        Estimated annual impact of manual policy monitoring
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Hours Lost Annually</p>
                      <p className="text-2xl md:text-3xl font-bold text-destructive">{metrics?.hoursLost?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Financial Impact</p>
                      <p className="text-2xl md:text-3xl font-bold text-destructive">${metrics?.financialImpact?.toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-success/10 border border-success/20 rounded-lg p-4 md:p-6">
                  <div className="flex items-start gap-3 mb-3">
                    <Icon name="CheckCircle2" size={24} color="var(--color-success)" />
                    <div className="flex-1">
                      <h4 className="text-base md:text-lg font-semibold text-foreground mb-1">
                        With PolicyAlerts
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        Projected savings and efficiency gains
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Time Saved Annually</p>
                      <p className="text-2xl md:text-3xl font-bold text-success">{metrics?.timeSaved?.toLocaleString()} hrs</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">ROI Percentage</p>
                      <p className="text-2xl md:text-3xl font-bold text-success">{metrics?.roiPercentage}%</p>
                    </div>
                  </div>
                </div>

                <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 md:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-base font-semibold text-foreground">Policy Coverage Risk Score</h4>
                    <span className="text-2xl md:text-3xl font-bold text-warning">{metrics?.riskScore}/100</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-3">
                    <div 
                      className="bg-warning h-3 rounded-full transition-all duration-500"
                      style={{ width: `${metrics?.riskScore}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">
                    Higher scores indicate greater risk of missing critical policy changes without automated monitoring
                  </p>
                </div>

                <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 md:p-6">
                  <h4 className="text-base font-semibold text-foreground mb-3">Key Benefits</h4>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">Automated tracking of all relevant policy changes</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">Real-time alerts for critical coverage updates</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">Reduced manual research and monitoring time</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">Comprehensive policy intelligence for strategic planning</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PolicyImpactCalculator;